import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, Heart, ArrowRight } from "lucide-react";

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      try {
        const res = await api.get(`/donations/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setAmount(res.data.amount);
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("failed");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="donation-success-page" className="min-h-[70vh] grid place-items-center px-5 py-20">
      <div className="max-w-lg w-full rounded-3xl border border-slate-100 bg-white p-10 text-center shadow-xl">
        {state === "checking" && (
          <div data-testid="donation-checking">
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-accent" />
            <h1 className="mt-6 font-display text-2xl font-black text-brandnavy">Confirming your donation…</h1>
            <p className="mt-2 text-slate-600">Please wait a moment while we verify your payment.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="donation-success">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-500" />
            <h1 className="mt-6 font-display text-3xl font-black text-brandnavy">Thank you for your generosity!</h1>
            {amount != null && (
              <p className="mt-2 text-lg font-semibold text-accent">${Number(amount).toFixed(2)} donated</p>
            )}
            <p className="mt-3 text-slate-600">
              Your gift directly funds confidence, structure, and opportunity for underserved youth. We're deeply grateful.
            </p>
            <Link to="/" data-testid="donation-home-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brandnavy px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5">
              Back to Home <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div data-testid="donation-failed">
            <XCircle className="mx-auto h-16 w-16 text-slate-400" />
            <h1 className="mt-6 font-display text-2xl font-black text-brandnavy">
              {state === "timeout" ? "Still processing…" : "Payment not completed"}
            </h1>
            <p className="mt-3 text-slate-600">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, you'll receive a confirmation. You can also check back shortly."
                : "It looks like the donation wasn't completed. You're welcome to try again whenever you're ready."}
            </p>
            <Link to="/support" data-testid="donation-retry-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-orange-400">
              <Heart className="h-4 w-4 fill-current" /> Try Again
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
